/**
 * The schema registry.
 *
 * Adding a form is adding a file here and a line below. Nothing in the engine or the renderer
 * needs to change, and nothing in either knows what a locker is.
 */

import { INSTALL } from './install.ts';
import { PROVISIONING } from './provisioning.ts';
import { SITE_PREP } from './site-prep.ts';
import { MAINTENANCE } from './maintenance.ts';
import { POST_INSTALL } from './post-install.ts';
import { accountFor } from '../payload.ts';
import { isTrustedSource } from '../prefill.ts';
import type { SchemaAccounting } from '../payload.ts';
import type { FormSchema } from '../types.ts';

export const SCHEMAS: readonly FormSchema[] = [INSTALL, PROVISIONING, SITE_PREP, MAINTENANCE, POST_INSTALL];

export function getSchema(id: string): FormSchema | undefined {
  return SCHEMAS.find((s) => s.id === id);
}

/**
 * The honest scoreboard.
 *
 * `asked` and `prefilled` are counted over the whole schema, ignoring display logic, because that
 * is the only denominator that cannot be gamed: adding a conditional question that is usually
 * hidden would improve a logic-aware ratio without removing any work.
 */
export function totals(): {
  asked: number;
  prefilled: number;
  reference: number;
  total: number;
  perSchema: SchemaAccounting[];
} {
  const perSchema = SCHEMAS.map((s) => accountFor(s, isTrustedSource));
  return {
    asked: perSchema.reduce((n, a) => n + a.asked, 0),
    prefilled: perSchema.reduce((n, a) => n + a.prefilled, 0),
    reference: perSchema.reduce((n, a) => n + a.reference, 0),
    total: perSchema.reduce((n, a) => n + a.total, 0),
    perSchema,
  };
}

export { INSTALL, PROVISIONING, SITE_PREP, MAINTENANCE, POST_INSTALL };
